import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { AxiosError } from 'axios'
import { Badge } from '@/components/ui/badge'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getEscalatedTicketsByEmployee } from '@/modules/Ticket/services'
import type { EscalatedTicketItem } from '@/modules/Ticket/types/response'
import type { ResponseArray } from '@/types/shared'
import { formatFromUTC } from '@/utils/function'

interface EscalatedTicketsDialogProps {
    employeeId: number
    employeeName: string
    startDate: string
    endDate: string
    open: boolean
    onClose: () => void
}

const PAGE_SIZE = 10

const EscalatedTicketsDialog: React.FC<EscalatedTicketsDialogProps> = ({
    employeeId,
    employeeName,
    startDate,
    endDate,
    open,
    onClose,
}) => {
    const { t } = useTranslation('ticket')
    const [page, setPage] = useState(1)

    // A different employee or a different range is a different list; without
    // this, reopening on someone else would land on whatever page was left over
    // from the previous employee.
    useEffect(() => {
        setPage(1)
    }, [employeeId, startDate, endDate, open])

    const datesValid = Boolean(startDate && endDate)

    const { data, renderState } = useQueryWithHandler<
        ResponseArray<EscalatedTicketItem>
    >(
        {
            queryKey: [
                QUERY_KEYS.TICKET_ESCALATED_DETAILS,
                employeeId,
                startDate,
                endDate,
                page,
                PAGE_SIZE,
            ],
            queryFn: () =>
                getEscalatedTicketsByEmployee({
                    employee_id: employeeId,
                    start_date: startDate,
                    end_date: endDate,
                    page,
                    page_size: PAGE_SIZE,
                }),
            enabled: open && employeeId > 0 && datesValid,
            // The global default is 5 minutes. This is a drill-down on a number
            // the user just read, so it must not answer from a cache that
            // predates the escalation they are checking.
            staleTime: 0,
        },
        {
            // The default error path renders a fixed sentence and never reads
            // error_description, so every backend error key would be invisible.
            // This maps the key through the ticket namespace instead.
            renderError: (error) => {
                const key = (
                    error as AxiosError<{ error_description?: string }>
                )?.response?.data?.error_description
                const message = key
                    ? t(`escalated.error.${key}`, {
                          defaultValue: t('escalated.error-generic'),
                      })
                    : t('escalated.error-generic')
                return (
                    <p className="py-8 text-center text-sm text-destructive">
                        {message}
                    </p>
                )
            },
        }
    )

    const items = data?.results ?? []
    const total = data?.pagination?.total ?? 0
    const stateComponent = renderState()

    const columns = useMemo<DataTableColumn<EscalatedTicketItem>[]>(
        () => [
            {
                key: 'ticket_id',
                title: t('escalated.col-ticket'),
                width: 80,
                render: (value: number) => (
                    <Link
                        to={`/tickets/${value}`}
                        className="text-blue-600 hover:underline"
                    >
                        #{value}
                    </Link>
                ),
            },
            {
                key: 'ticket_content',
                title: t('escalated.col-content'),
                width: 240,
                // Every text column truncates, and does so by filling the cell
                // rather than carrying a max-width equal to the column: the cell
                // is p-2, so a box as wide as the column overflows it by 16px.
                // TableCell sets no overflow of its own, and a column with a
                // custom render skips the table's default ellipsis wrapper, so
                // an untruncated value spills across its neighbours.
                render: (value: string) => (
                    <span className="block truncate" title={value}>
                        {value}
                    </span>
                ),
            },
            {
                key: 'ticket_type_title',
                title: t('escalated.col-type'),
                width: 140,
                render: (value: string) => (
                    <span className="block truncate" title={value}>
                        {value}
                    </span>
                ),
            },
            {
                key: 'ticket_requested_at',
                title: t('escalated.col-requested-at'),
                width: 130,
                // Timestamps arrive without a UTC offset, like everywhere else
                // in this API, so they must go through formatFromUTC.
                render: (value: string) => formatFromUTC(value),
            },
            {
                key: 'escalated_at',
                title: t('escalated.col-escalated-at'),
                width: 130,
                render: (value: string | null) => formatFromUTC(value),
            },
            {
                key: 'escalated_by_name',
                title: t('escalated.col-escalated-by'),
                width: 140,
                // Empty rather than null when the log has no author.
                render: (value: string) => (
                    <span className="block truncate" title={value}>
                        {value || t('escalated.unknown-person')}
                    </span>
                ),
            },
            {
                key: 'reason',
                title: t('escalated.col-reason'),
                width: 200,
                render: (value: string) => (
                    <span className="block truncate" title={value}>
                        {value}
                    </span>
                ),
            },
            {
                key: 'ticket_status',
                title: t('escalated.col-status'),
                width: 110,
                render: (value: string) => (
                    <Badge variant="secondary" className="max-w-full truncate">
                        {value}
                    </Badge>
                ),
            },
        ],
        [t]
    )

    return (
        <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
            <DialogContent className="w-[95vw] max-w-6xl">
                <DialogHeader>
                    <DialogTitle>
                        {t('escalated.modal-title', { name: employeeName })}
                    </DialogTitle>
                    <DialogDescription>
                        {t('escalated.modal-description', {
                            total,
                            from: startDate,
                            to: endDate,
                        })}
                    </DialogDescription>
                </DialogHeader>

                <p className="text-xs text-muted-foreground">
                    {t('escalated.latest-only-note')}
                </p>

                {/* DialogContent is a grid, so this child would otherwise get
                    min-width:auto and refuse to shrink below the table's own
                    minimum width. min-width beats max-width, so the dialog would
                    stretch past the viewport instead of the table scrolling
                    inside it. */}
                <div className="min-w-0">
                    {stateComponent ?? (
                        <DataTableCustomized
                            columns={columns}
                            rows={items}
                            rowKey={(row) => String(row.ticket_id)}
                            size="small"
                            emptyMessage={t('escalated.empty')}
                            // Keeps a full page of rows from pushing the dialog
                            // past the bottom of the screen; the header stays
                            // put while the rows scroll.
                            maxHeight="55vh"
                            pinnedHeader
                            pagination={{
                                page,
                                pageSize: PAGE_SIZE,
                                // Server-side: total comes from the API, which
                                // is the source of truth for this drill-down.
                                total,
                                onPageChange: setPage,
                            }}
                        />
                    )}
                </div>

                <p className="text-xs text-muted-foreground">
                    {t('escalated.provenance-note')}
                </p>
            </DialogContent>
        </Dialog>
    )
}

export default EscalatedTicketsDialog
